import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PublicHeader } from "@/components/layout/AppChrome";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Mail, Send } from "lucide-react";

/**
 * Verify email page — used in two distinct ways:
 *
 *   1. Magic-link consumer (`?token=...` present) — the user clicked the
 *      link in their inbox. We POST /auth/verify-email {token} and show
 *      success or error.
 *
 *   2. Verification landing (no `?token`) — the frontend interceptor
 *      navigated an authenticated-but-unverified user here after they
 *      hit a gated endpoint. We show a friendly "check your inbox"
 *      state with a resend button. This is the fix for the regression
 *      where logged-in users were bounced here without a token and
 *      saw a confusing "Missing verification token" error.
 */
export default function VerifyEmailPage() {
  const { lang } = useI18n();
  const loc = useLocation();
  const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  // States: pending (no token — prompt user) | verifying | success | error
  const [state, setState] = useState(token ? "verifying" : "pending");
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(0);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
      } catch (e) {
        setState("error");
        setMessage(formatApiError(e));
      }
    })();
  }, [token]);

  async function resendVerification() {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", {});
      setResentAt(Date.now());
    } catch (e) {
      setMessage(formatApiError(e));
    } finally {
      setResending(false);
    }
  }

  const t = {
    ar: {
      verifying: "جاري التحقّق من بريدك...",
      successTitle: "تم تفعيل بريدك ✓",
      successBody: "شكراً لك! حسابك الآن مُفعّل بالكامل ويمكنك الاستفادة من جميع مميّزات دعم.",
      goDashboard: "الانتقال إلى لوحة التحكم",
      errorTitle: "تعذّر التحقّق",
      backLogin: "العودة لتسجيل الدخول",
      pendingTitle: "تأكيد بريدك الإلكتروني",
      pendingBody: "أرسلنا رسالة إلى بريدك تحتوي على رابط التفعيل. افتح البريد واضغط على الرابط لإكمال التفعيل.",
      resendBtn: "إعادة إرسال الرسالة",
      resent: "تم إرسال الرابط مرة أخرى ✓ تحقّق من صندوق الوارد.",
      resending: "جاري الإرسال...",
    },
    en: {
      verifying: "Verifying your email...",
      successTitle: "Email verified ✓",
      successBody: "Thank you! Your account is now fully activated with all DAAM features unlocked.",
      goDashboard: "Go to dashboard",
      errorTitle: "Verification failed",
      backLogin: "Back to sign in",
      pendingTitle: "Verify your email",
      pendingBody: "We sent an email with a verification link to your inbox. Open it and click the link to activate your account.",
      resendBtn: "Resend verification email",
      resent: "Verification link sent again ✓ Check your inbox.",
      resending: "Sending...",
    },
  }[lang];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md nx-card p-8 md:p-10 text-center animate-fade-in-up" data-testid="verify-email-panel">
          {state === "pending" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-sm bg-[#B8875D]/10 flex items-center justify-center mb-4">
                <Mail className="w-8 h-8 text-[#B8875D]" />
              </div>
              <h1 className="font-display text-2xl mb-3" data-testid="verify-pending-title">{t.pendingTitle}</h1>
              <p className="text-sm text-muted-foreground mb-6">{t.pendingBody}</p>
              <button
                data-testid="verify-resend-btn"
                onClick={resendVerification}
                disabled={resending}
                className="nx-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
                {resending ? t.resending : t.resendBtn}
              </button>
              {resentAt > 0 && (
                <p data-testid="verify-resend-success" className="mt-4 text-sm text-emerald-600">{t.resent}</p>
              )}
              {message && !resentAt && (
                <p data-testid="verify-resend-error" className="mt-4 text-sm text-destructive">{message}</p>
              )}
              <div className="mt-6">
                <Link to="/login" data-testid="verify-back-link" className="text-sm text-muted-foreground hover:text-primary">{t.backLogin}</Link>
              </div>
            </>
          )}
          {state === "verifying" && (
            <>
              <Loader2 className="w-10 h-10 mx-auto text-[#B8875D] animate-spin mb-4" />
              <h1 className="font-display text-2xl">{t.verifying}</h1>
            </>
          )}
          {state === "success" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-sm bg-emerald-500/10 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="font-display text-2xl mb-3" data-testid="verify-success-title">{t.successTitle}</h1>
              <p className="text-sm text-muted-foreground mb-6">{t.successBody}</p>
              <button data-testid="verify-dashboard-btn" onClick={() => nav("/app/dashboard")} className="nx-btn-primary w-full">{t.goDashboard}</button>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-sm bg-destructive/10 flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <h1 className="font-display text-2xl mb-3" data-testid="verify-error-title">{t.errorTitle}</h1>
              <p className="text-sm text-muted-foreground mb-6">{message}</p>
              <Link to="/login" data-testid="verify-back-link" className="nx-btn-secondary inline-block">{t.backLogin}</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
